import { useEffect, useState } from 'react';
import type { ExportJobSettings, JobDefinition } from '../shared/desktop-api';
import { jobLearningAIQ } from './job-learning-aiq';
import { watchedLook } from './watched-folder-words';
// PAIGE: the host's own export and watched folders (host-export.ts, host-capabilities.ts).
import { hostHas } from '../shared/host-capabilities';
import { hostExportPlaces } from './host-export';

/** Live values shown on the job door's setup tiles. Absent until measured. */
export type JobDoorValues = {
  aiq?: number;
  lists?: number;
  destination?: string | null;
  /** When documents leave, and whether export is on, from the saved export settings. */
  when?: Pick<ExportJobSettings, 'triggerMode' | 'scheduleLocalTime' | 'enabled'>;
  /** The watched folder in a word or two: "Not set", "Paused", "Watching"… */
  watched?: string;
  /** PAIGE: where the job sends documents, from the host's destinations (host-export.ts). */
  sendsTo?: string;
};

/**
 * Reads the job's slower values only while its door is open. Each value stays
 * undefined until its own read succeeds, so a tile never shows a guess.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function useJobDoorValues(job: JobDefinition | undefined, open: boolean): JobDoorValues {
  const [reading, setReading] = useState<{ jobId: string; values: JobDoorValues }>({
    jobId: '',
    values: {},
  });
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!open || !job || !api) return;
    let live = true;
    const jobId = job.id;
    const put = (patch: JobDoorValues) => {
      if (live)
        setReading((current) => ({
          jobId,
          values: { ...(current.jobId === jobId ? current.values : {}), ...patch },
        }));
    };
    void api
      .getLearningCapability(jobId)
      .then((capability) => {
        const measured = jobLearningAIQ(job, [], { capability });
        if (measured) put({ aiq: measured.score });
      })
      .catch(() => undefined);
    void api
      .listDataTables(jobId)
      .then((catalog) => put({ lists: catalog.tables.length }))
      .catch(() => undefined);
    // PAIGE: the desktop's export summary and settings (`desktop-export`), or the host's destinations.
    if (!hostHas('desktop-export'))
      void hostExportPlaces()
        .then((words) => {
          if (words) put({ sendsTo: words });
        })
        .catch(() => undefined);
    else {
      void api
        .getJobWorkSummary(jobId)
        .then((summary) => put({ destination: summary.destinationLabel }))
        .catch(() => undefined);
      void api
        .getExportSettings(jobId)
        .then((settings) => {
          if (settings)
            put({
              when: {
                triggerMode: settings.triggerMode,
                scheduleLocalTime: settings.scheduleLocalTime,
                enabled: settings.enabled,
              },
            });
        })
        .catch(() => undefined);
    }
    if (hostHas('watched-folder') && api.getWatchedFolder && api.watchedFolderStatus)
      void Promise.all([api.getWatchedFolder(jobId), api.watchedFolderStatus()])
        .then(([setting, all]) =>
          put({
            watched: setting
              ? watchedLook(
                  all.find((entry) => entry.jobId === jobId),
                  setting.enabled,
                ).label
              : 'Not set',
          }),
        )
        .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [job?.id, open]);
  return reading.jobId === job?.id ? reading.values : {};
}
